/**
 * Mecanismo unificado de priming de permissões de dispositivo (câmera/microfone).
 *
 * Ao contrário de `getUserMedia` direto (que dispara o prompt do sistema sem contexto),
 * este hook mostra um sheet in-app explicando **por que** o app precisa do recurso
 * antes do prompt do SO, e trata o estado "negado/bloqueado" orientando o usuário às
 * Configurações do dispositivo. Espelha o padrão de priming das notificações
 * (`usePushRegistration` / `AppPermissionsCard`).
 *
 * Paridade web/nativo:
 * - Web: `navigator.permissions.query` para estado; `getUserMedia` para solicitar.
 * - Nativo (Capacitor): câmera → `Camera.checkPermissions`/`requestPermissions`;
 *   microfone → `VoiceRecorder.hasAudioRecordingPermission`/`requestAudioRecordingPermission`.
 *
 * Máquina de estados de `requestDeviceAccess(kind, { reason })`:
 * - estado atual `granted`  → resolve `'granted'` imediatamente (NÃO abre sheet).
 * - estado `prompt`/desconhecido (1º uso) → abre sheet em modo "priming";
 *   ao confirmar dispara o pedido real: sucesso → `'granted'`; recusa → `'denied'`
 *   (sheet troca para orientação de Configurações). "Agora não" → `'denied'`.
 * - estado `denied`/bloqueado → abre sheet em modo "blocked" (orientação) → `'blocked'`.
 */

import { useCallback, useRef, useState } from 'react'
import { platform } from '@/shared/lib/platform/platform'
import {
  nativeCameraPermissionState,
  nativeHasMicrophonePermission,
  nativeRequestCameraPermission,
  nativeRequestMicrophonePermission,
} from '@/shared/lib/platform/devicePermissions'

export type DeviceKind = 'camera' | 'microphone'

/** Resultado final da solicitação de acesso. */
export type DeviceAccessOutcome = 'granted' | 'denied' | 'blocked'

/** Estado bruto da permissão consultado antes de solicitar. */
export type DevicePermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

export interface RequestDeviceAccessOptions {
  /** Racional pt-BR exibido no sheet (por que o app precisa do recurso). */
  reason: string
}

/** Estado interno do sheet de priming, consumido pela UI. */
export interface DevicePrimingSheetState {
  open: boolean
  kind: DeviceKind
  reason: string
  /** `priming` = 1º uso (pedir); `blocked` = negado, orientar Configurações. */
  mode: 'priming' | 'blocked'
  /** Solicitação em andamento (desabilita ações). */
  busy: boolean
}

/** Mapeia o estado nativo do Capacitor para o vocabulário unificado. */
function mapNativePermissionState(state: string | undefined): DevicePermissionState {
  if (state === 'granted' || state === 'limited') return 'granted'
  if (state === 'denied') return 'denied'
  // 'prompt' | 'prompt-with-rationale' | undefined
  return 'prompt'
}

/**
 * Consulta o estado atual da permissão sem disparar o prompt do SO.
 * Exportada para teste direto da lógica de paridade web/nativo.
 */
export async function queryDevicePermission(kind: DeviceKind): Promise<DevicePermissionState> {
  if (platform.isNative()) {
    try {
      if (kind === 'camera') {
        return mapNativePermissionState(await nativeCameraPermissionState())
      }
      // O plugin não distingue "prompt" de "blocked"; tratamos ausência como "prompt"
      // e deixamos o resultado do pedido real classificar como "denied".
      return (await nativeHasMicrophonePermission()) ? 'granted' : 'prompt'
    } catch {
      return 'unsupported'
    }
  }

  const name = kind === 'camera' ? 'camera' : 'microphone'
  if (typeof navigator?.permissions?.query !== 'function') {
    return 'unsupported'
  }
  try {
    const status = await navigator.permissions.query({ name } as PermissionDescriptor)
    return status.state as DevicePermissionState
  } catch {
    return 'unsupported'
  }
}

/**
 * Dispara o pedido real de permissão (mostra o prompt do SO na 1ª vez).
 * Retorna `true` quando concedido. Exportada para teste direto.
 */
export async function requestDevicePermission(kind: DeviceKind): Promise<boolean> {
  if (platform.isNative()) {
    try {
      if (kind === 'camera') {
        const state = await nativeRequestCameraPermission()
        return state === 'granted' || state === 'limited'
      }
      return await nativeRequestMicrophonePermission()
    } catch {
      return false
    }
  }

  if (!navigator?.mediaDevices?.getUserMedia) return false
  try {
    const constraints: MediaStreamConstraints =
      kind === 'camera' ? { video: true } : { audio: true }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    // Encerra as tracks: o priming só valida a permissão, não usa a mídia.
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch {
    return false
  }
}

/**
 * Abre as Configurações do dispositivo (best-effort, apenas nativo).
 * Sem plugin de settings instalado, retorna `false` e a UI mantém a orientação
 * textual. O deep-link nativo é habilitado quando o plugin existir (T-G4).
 */
export async function openDeviceSettings(): Promise<boolean> {
  return false
}

export interface UseDevicePermissionRequestResult {
  /** Solicita acesso; orquestra sheet + prompt e resolve o desfecho. */
  requestDeviceAccess: (
    kind: DeviceKind,
    opts: RequestDeviceAccessOptions,
  ) => Promise<DeviceAccessOutcome>
  /** Estado do sheet (null quando fechado). Alimente `<PermissionPrimingSheet>`. */
  sheet: DevicePrimingSheetState | null
  /** Confirmação do priming ("Permitir"). */
  confirm: () => Promise<void>
  /** Recusa/fechamento ("Agora não" / scrim / Esc). */
  dismiss: () => void
  /** Ação do modo bloqueado ("Abrir Configurações"). */
  openSettings: () => Promise<void>
}

export function useDevicePermissionRequest(): UseDevicePermissionRequestResult {
  const [sheet, setSheetState] = useState<DevicePrimingSheetState | null>(null)
  const sheetRef = useRef<DevicePrimingSheetState | null>(null)
  const resolverRef = useRef<((outcome: DeviceAccessOutcome) => void) | null>(null)

  // Mantém um espelho síncrono do sheet: `confirm`/`openSettings` são disparados
  // por callbacks da UI *após* o render, então leem `sheetRef.current` de forma
  // confiável (o updater de estado do React só roda durante o render seguinte).
  const setSheet = useCallback(
    (
      next:
        | DevicePrimingSheetState
        | null
        | ((prev: DevicePrimingSheetState | null) => DevicePrimingSheetState | null),
    ) => {
      setSheetState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next
        sheetRef.current = resolved
        return resolved
      })
    },
    [],
  )

  const settle = useCallback((outcome: DeviceAccessOutcome) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    resolve?.(outcome)
  }, [])

  const requestDeviceAccess = useCallback(
    (kind: DeviceKind, opts: RequestDeviceAccessOptions): Promise<DeviceAccessOutcome> => {
      // Se houver uma solicitação pendente, resolve como recusa antes de iniciar outra.
      if (resolverRef.current) settle('denied')

      const promise = new Promise<DeviceAccessOutcome>((resolve) => {
        resolverRef.current = resolve
      })

      void (async () => {
        const state = await queryDevicePermission(kind)

        if (state === 'granted') {
          // Já concedido: nunca reabre o sheet.
          setSheet(null)
          settle('granted')
          return
        }

        if (state === 'denied') {
          // Bloqueado no SO: orienta Configurações e resolve imediatamente.
          setSheet({ open: true, kind, reason: opts.reason, mode: 'blocked', busy: false })
          settle('blocked')
          return
        }

        // 'prompt' | 'unsupported' → 1º uso: mostra racional antes do prompt.
        setSheet({ open: true, kind, reason: opts.reason, mode: 'priming', busy: false })
      })()

      return promise
    },
    [settle, setSheet],
  )

  const confirm = useCallback(async () => {
    const current = sheetRef.current
    if (!current) return
    setSheet({ ...current, busy: true })

    const granted = await requestDevicePermission(current.kind)
    if (granted) {
      setSheet(null)
      settle('granted')
      return
    }
    // Recusa fresca: troca para orientação de Configurações; desfecho "denied".
    setSheet({ ...current, mode: 'blocked', busy: false })
    settle('denied')
  }, [settle, setSheet])

  const dismiss = useCallback(() => {
    setSheet(null)
    settle('denied')
  }, [settle, setSheet])

  const openSettings = useCallback(async () => {
    await openDeviceSettings()
    setSheet(null)
    settle('blocked')
  }, [settle, setSheet])

  return { requestDeviceAccess, sheet, confirm, dismiss, openSettings }
}
